/**
 * Utility functions for ACE Editor mode handling
 * 
 * This module provides helper functions to map stored language values 
 * to valid ACE Editor mode names. This is necessary because some 
 * language values stored in the database don't match the ACE Editor 
 * mode names (e.g., 'c++' should be 'c_cpp' for ACE).
 */

/**
 * Maps a stored code_language value to the correct ACE Editor mode name.
 * 
 * @param codeLanguage - The code_language value stored in the database/data
 * @returns The valid ACE Editor mode name
 * 
 * @example
 * getAceMode('c++') // returns 'c_cpp'
 * getAceMode('javascript') // returns 'javascript'
 * getAceMode('kotlin (1.3.70)') // returns 'kotlin'
 */
export function getAceMode(codeLanguage: string): string {
    if (!codeLanguage) {
        return 'text'; // Default fallback mode
    }

    const lang = codeLanguage.toLowerCase().trim();

    // Map non-standard language values to valid ACE mode names
    const modeMap: { [key: string]: string } = {
        // C++ variants - ACE uses 'c_cpp' for both C and C++
        'c++': 'c_cpp',
        'cpp': 'c_cpp',

        // Kotlin with version suffix
        'kotlin (1.3.70)': 'kotlin',

        // Go language - ACE uses 'golang'
        'go': 'golang',
    };

    // Check if we have a mapping for this language
    if (modeMap[lang]) {
        return modeMap[lang];
    }

    // For most languages, the stored value matches the ACE mode name
    return lang;
}

/**
 * Generates the full ACE Editor mode path from a code_language value.
 * 
 * @param codeLanguage - The code_language value stored in the database/data
 * @returns The full ACE Editor mode path (e.g., 'ace/mode/c_cpp')
 * 
 * @example
 * getAceModePath('c++') // returns 'ace/mode/c_cpp'
 * getAceModePath('javascript') // returns 'ace/mode/javascript'
 */
export function getAceModePath(codeLanguage: string): string {
    return `ace/mode/${getAceMode(codeLanguage)}`;
}

/**
 * Determines if a given language mode should use a background worker (for linting/syntax checks).
 * 
 * @param mode - The ACE mode name (e.g., 'javascript', 'c_cpp')
 * @returns boolean indicating if a worker should be used
 */
export function shouldUseWorker(mode: string): boolean {
    const languagesWithWorkers = ['javascript', 'json', 'css', 'html', 'typescript', 'php'];
    return languagesWithWorkers.includes(mode);
}

/**
 * Configures an ACE session with the correct mode and worker settings based on the language.
 * This helper ensures that workers (like the JavaScript linter) are correctly disabled 
 * when switching to languages like C/C++ to avoid incorrect error markers.
 * 
 * @param session - The ACE editor session to configure
 * @param codeLanguage - The code_language value from the database
 */
export function configureAceSession(session: any, codeLanguage: string): void {
    if (!session) return;

    const mode = getAceMode(codeLanguage);
    session.setMode(`ace/mode/${mode}`);
    session.setUseWorker(shouldUseWorker(mode));
}
